// lib/administracja/komunikaty.ts — CO POWIEDZIEĆ OPERATOROWI PO NIEUDANYM ZAPISIE.
//
// Trzy różne sytuacje, których nie wolno zlać w jedno „nie udało się":
//   • ODMOWA — Control Center albo system główny powiedział „nie" (zła kwota, złe hasło, para już jest).
//     Nic się nie zmieniło; komunikat systemu głównego idzie do operatora dosłownie.
//   • WYNIK NIEZNANY — brak odpowiedzi w czasie, zerwane połączenie, 5xx. Operacja MOGŁA się wykonać.
//     Operator ma najpierw sprawdzić stan (lista odświeża się sama), a nie klikać drugi raz na ślepo.
//   • BRAK DOSTĘPU — 401/403: nic nie wysłano do systemu głównego.
import { ApiError } from '@/lib/api/errors';

interface CialoBledu {
  error?: string;
  szczegoly?: { status?: number; komunikat?: string | null; kod?: string | null } | null;
  correlation_id?: string;
}

export interface OpisBledu {
  /** Zdanie dla operatora. */
  tekst: string;
  /** `true` = operacja MOGŁA się wykonać — przed ponowieniem trzeba sprawdzić stan. */
  wynikNieznany: boolean;
  /** Identyfikator zgłoszenia do przekazania obsłudze (bez danych osobowych). */
  korelacja: string | null;
}

export function opisBleduOperacji(e: unknown): OpisBledu {
  if (!(e instanceof ApiError)) {
    return { tekst: 'Nieoczekiwany błąd aplikacji — wynik operacji nieznany. Odśwież widok i sprawdź stan.', wynikNieznany: true, korelacja: null };
  }
  const cialo = (e.body ?? {}) as CialoBledu;
  const korelacja = typeof cialo.correlation_id === 'string' ? cialo.correlation_id : null;
  const zSystemu = cialo.szczegoly?.komunikat ?? null;
  const kod = cialo.szczegoly?.kod ?? null;

  if (e.kind === 'network') {
    return {
      tekst: 'Brak odpowiedzi z Control Center (połączenie przerwane albo przekroczony czas) — wynik operacji NIEZNANY. '
        + 'Odśwież widok i sprawdź stan, zanim spróbujesz ponownie.',
      wynikNieznany: true,
      korelacja,
    };
  }
  if (e.kind === 'unauthorized') {
    return { tekst: 'Sesja wygasła — zaloguj się ponownie. Operacja nie została wysłana.', wynikNieznany: false, korelacja };
  }
  if (e.kind === 'forbidden') {
    return { tekst: 'Twoje konto nie ma uprawnień do tej operacji. Nic nie zostało zmienione.', wynikNieznany: false, korelacja };
  }
  if (e.status === 503) {
    return {
      tekst: cialo.error ?? 'Zapis nie jest w tym środowisku podłączony do systemu głównego. Nic nie zostało zmienione.',
      wynikNieznany: false,
      korelacja,
    };
  }
  if (e.status !== null && e.status >= 500) {
    const powod = cialo.error ?? `Błąd serwera (${e.status}).`;
    return {
      tekst: `${powod} Wynik operacji NIEZNANY — odśwież widok i sprawdź stan, zanim spróbujesz ponownie.`,
      wynikNieznany: true,
      korelacja,
    };
  }
  // 4xx: odmowa — jawna, z komunikatem systemu głównego, gdy go podał.
  const czesci = [cialo.error ?? `Operacja odrzucona (${e.status ?? '4xx'}).`];
  if (zSystemu) czesci.push(`System główny: ${zSystemu}${kod ? ` (${kod})` : ''}`);
  return { tekst: `${czesci.join(' ')} Nic nie zostało zmienione.`, wynikNieznany: false, korelacja };
}

/**
 * Dopowiedzenie do błędu ODCZYTU: powód podany przez serwer CC (np. „System główny nie odpowiedział
 * w wyznaczonym czasie.", „Działania operacyjne nie są w tym środowisku podłączone."). Ogólny opis
 * rodzaju błędu daje `ErrorState`; ten tekst mówi, CO konkretnie zawiodło. 5xx nigdy nie jest „brakiem danych".
 */
export function powodBleduOdczytu(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const cialo = (e.body ?? {}) as CialoBledu;
  const czesci = [cialo.error, cialo.szczegoly?.komunikat ? `System główny: ${cialo.szczegoly.komunikat}` : null].filter(Boolean);
  return czesci.length ? czesci.join(' ') : null;
}

/** Losowy identyfikator operacji (UUID v4) — klucz idempotencji doładowania, jeden na otwarcie okna. */
export function nowyIdentyfikatorOperacji(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Górna granica kwoty = zakres pola `Amount` w systemie głównym (int, grosze) — 21 474 836,47 zł.
 * To NIE jest limit biznesowy Control Center (decyzja OWNERA D-ADM-2: CC nie dodaje własnego limitu,
 * bo nie ma go ani stary panel, ani system główny); większej liczby system główny po prostu nie przyjmie.
 */
export const KWOTA_MAKS_GROSZE = 2_147_483_647;

/**
 * Kwota z pola formularza → grosze, tymi samymi regułami co serwer CC (`kwotaWGroszach`):
 * złote i najwyżej 2 miejsca po przecinku, więcej niż zero, w zakresie systemu głównego
 * (`KWOTA_MAKS_GROSZE`). `null` = niepoprawna.
 * Służy do PODGLĄDU „saldo po" przed potwierdzeniem — ostatnie słowo i tak ma serwer.
 */
export function kwotaDoGroszy(tekst: string): number | null {
  const m = /^\s*(\d{1,8})(?:[.,](\d{1,2}))?\s*(?:zł)?\s*$/i.exec(tekst);
  if (!m) return null;
  const grosze = Number(m[1]) * 100 + Number((m[2] ?? '0').padEnd(2, '0'));
  return grosze > 0 && grosze <= KWOTA_MAKS_GROSZE ? grosze : null;
}

/**
 * Próg ostrzeżenia „Wysokie doładowanie — sprawdź kwotę." (500 zł). Tylko ostrzeżenie — NIE blokuje
 * operacji; serwer CC ani system główny go nie znają (decyzja OWNERA D-ADM-2, 10.10.2026).
 */
export const KWOTA_DO_SPRAWDZENIA_GROSZE = 50_000;

/** Treść ostrzeżenia przy wysokim doładowaniu (OWNER 10.10.2026, dosłownie). */
export const OSTRZEZENIE_WYSOKIEJ_KWOTY = 'Wysokie doładowanie — sprawdź kwotę.';
